import { defineStore } from 'pinia';
import { useFilterStore } from 'src/stores/filter';
import { ACTIVE_FAVORITES_BY_MODULE, SEARCH_HISTORY_BY_MODULE } from 'src/stores/searchHistory/localStorageKeys';
import createSearchHistoryState, { SearchHistoryStateInterface } from 'src/stores/searchHistory/state';

import { SearchHistoryByModule, SearchHistoryEntry } from 'src/models/searchHistory';

import { StateProviderService } from 'src/services/stateProvider';

import { instanceSpecificLocalStorageKey } from 'src/util/common';

import type {
	AddSearchEntryPayload,
	ClearAllHistoryPayload,
	ClearNonFavoriteHistoryPayload,
	LoadFromBackendPayload,
	RemoveSearchEntryPayload,
	ReorderFavoritesPayload,
	SaveToBackendPayload,
	SetActiveFavoritePayload,
	ToggleFavoritePayload,
	UpdateFavoriteCompletePayload,
	UpdateFavoriteMetadataPayload,
} from './interfaces';
import type { BackendSyncStatus, ClearModuleHistoryPayload } from './types';

const MAX_NORMAL_SEARCH_ENTRIES_PER_MODULE = 10;
const EVENT_MARKERS = ['FAVORITE_TOGGLE', 'FAVORITES_ACTIVE'];

const persistActiveFavoritesByModule = (state: SearchHistoryStateInterface): void => {
	localStorage.setItem(
		instanceSpecificLocalStorageKey(ACTIVE_FAVORITES_BY_MODULE),
		JSON.stringify(state.activeFavoritesByModule)
	);
};

const persistHistoryByModule = (state: SearchHistoryStateInterface): void => {
	localStorage.setItem(
		instanceSpecificLocalStorageKey(SEARCH_HISTORY_BY_MODULE),
		JSON.stringify(state.historyByModule)
	);
};

const applyAddSearchEntry = (state: SearchHistoryStateInterface, payload: SearchHistoryEntry): void => {
	const { color, displayInResults, isFavorite, label, module, query, timestamp } = payload;

	if (EVENT_MARKERS.includes(query)) {
		console.warn(`Event-System-Marker '${query}' wird nicht in die Suchhistorie eingetragen`);
		return;
	}

	if (!query || query.trim() === '') {
		return;
	}

	if (!state.historyByModule[module]) {
		state.historyByModule[module] = [];
	}

	const existingEntryIndex = state.historyByModule[module].findIndex((entry) => entry.query === query);

	if (existingEntryIndex !== -1) {
		const existingEntry = state.historyByModule[module][existingEntryIndex];
		state.historyByModule[module][existingEntryIndex] = {
			color: color !== undefined ? color : existingEntry.color,
			displayInResults: displayInResults !== undefined ? displayInResults : existingEntry.displayInResults,
			isFavorite: isFavorite !== undefined ? isFavorite : existingEntry.isFavorite,
			label: label !== undefined ? label : existingEntry.label,
			module,
			query,
			timestamp,
		};

		const updatedEntry = state.historyByModule[module].splice(existingEntryIndex, 1)[0];
		state.historyByModule[module].unshift(updatedEntry);
	} else {
		let sortOrder: number | undefined;
		if (isFavorite) {
			const existingFavorites = state.historyByModule[module].filter((entry) => entry.isFavorite === true);
			const maxSortOrder = Math.max(...existingFavorites.map((favorite) => favorite.sortOrder ?? -1), -1);
			sortOrder = maxSortOrder + 1;
		}

		state.historyByModule[module].unshift({
			color,
			displayInResults,
			isFavorite,
			label,
			module,
			query,
			sortOrder,
			timestamp,
		});
	}

	const favorites = state.historyByModule[module].filter((entry) => entry.isFavorite);
	const nonFavorites = state.historyByModule[module].filter((entry) => !entry.isFavorite);

	const limitedNonFavorites = nonFavorites.slice(0, MAX_NORMAL_SEARCH_ENTRIES_PER_MODULE);

	state.historyByModule[module] = [...favorites, ...limitedNonFavorites].sort((a, b) => b.timestamp - a.timestamp);

	persistHistoryByModule(state);
};

const applyCleanEventMarkers = (state: SearchHistoryStateInterface): void => {
	Object.keys(state.historyByModule).forEach((module) => {
		state.historyByModule[module] = state.historyByModule[module].filter(
			(entry) => !EVENT_MARKERS.includes(entry.query)
		);
	});

	persistHistoryByModule(state);
};

const applyClearAllHistory = (state: SearchHistoryStateInterface): void => {
	state.historyByModule = {};
	localStorage.removeItem(instanceSpecificLocalStorageKey(SEARCH_HISTORY_BY_MODULE));
};

const applyClearModuleHistory = (state: SearchHistoryStateInterface, module: string): void => {
	if (state.historyByModule[module]) {
		delete state.historyByModule[module];
		persistHistoryByModule(state);
	}
};

const applyClearNonFavoriteHistory = (state: SearchHistoryStateInterface, module: string): void => {
	if (state.historyByModule[module]) {
		state.historyByModule[module] = state.historyByModule[module].filter((entry) => entry.isFavorite === true);

		if (state.historyByModule[module].length === 0) {
			delete state.historyByModule[module];
		}

		persistHistoryByModule(state);
	}
};

const applyLoadHistoryFromStorage = (state: SearchHistoryStateInterface, payload: SearchHistoryByModule): void => {
	const cleanedPayload: SearchHistoryByModule = {};

	Object.keys(payload).forEach((module) => {
		cleanedPayload[module] = payload[module].filter((entry) => !EVENT_MARKERS.includes(entry.query));
	});

	state.historyByModule = cleanedPayload;
	persistHistoryByModule(state);
};

const applyMergeBackendData = (
	state: SearchHistoryStateInterface,
	payload: {
		historyByModule: SearchHistoryByModule;
	}
): void => {
	const mergedHistoryByModule: SearchHistoryByModule = {
		...state.historyByModule,
	};

	Object.keys(payload.historyByModule).forEach((module) => {
		const backendEntries = payload.historyByModule[module] || [];
		const mergedEntriesMap = new Map<string, SearchHistoryEntry>();

		backendEntries.forEach((entry) => {
			mergedEntriesMap.set(entry.query, entry);
		});

		mergedHistoryByModule[module] = Array.from(mergedEntriesMap.values()).sort((a, b) => b.timestamp - a.timestamp);
	});

	Object.keys(state.historyByModule).forEach((key) => {
		delete state.historyByModule[key];
	});

	Object.keys(mergedHistoryByModule).forEach((key) => {
		state.historyByModule[key] = mergedHistoryByModule[key];
	});

	persistHistoryByModule(state);
};

const applyRemoveSearchEntry = (state: SearchHistoryStateInterface, payload: RemoveSearchEntryPayload): void => {
	const { module, query } = payload;

	if (state.historyByModule[module]) {
		state.historyByModule[module] = state.historyByModule[module].filter(
			(entry) => !(entry.query === query && !entry.isFavorite)
		);
		persistHistoryByModule(state);
	}
};

const applyReorderFavorites = (
	state: SearchHistoryStateInterface,
	payload: {
		module: string;
		reorderedFavorites: SearchHistoryEntry[];
	}
): void => {
	if (state.historyByModule[payload.module]) {
		payload.reorderedFavorites.forEach((favorite, index) => {
			const entry = state.historyByModule[payload.module].find(
				(existingEntry) => existingEntry.query === favorite.query
			);
			if (entry && entry.isFavorite) {
				entry.sortOrder = index;
			}
		});

		persistHistoryByModule(state);
	}
};

const applyClearActiveFavoritesForModule = (state: SearchHistoryStateInterface, module: string): void => {
	state.activeFavoritesByModule[module] = [];
	persistActiveFavoritesByModule(state);
};

const applySetActiveFavorite = (state: SearchHistoryStateInterface, payload: SetActiveFavoritePayload): void => {
	if (!state.activeFavoritesByModule[payload.module]) {
		state.activeFavoritesByModule[payload.module] = [];
	}

	if (payload.favorite === null) {
		state.activeFavoritesByModule[payload.module] = [];
	} else {
		const existingIndex = state.activeFavoritesByModule[payload.module].findIndex(
			(favorite) => favorite.query === payload.favorite?.query
		);

		if (existingIndex !== -1) {
			state.activeFavoritesByModule[payload.module].splice(existingIndex, 1);
		} else {
			state.activeFavoritesByModule[payload.module].push(payload.favorite);
		}
	}

	persistActiveFavoritesByModule(state);
};

const applySetBackendSyncStatus = (
	state: SearchHistoryStateInterface,
	payload: {
		status: BackendSyncStatus;
		timestamp?: number;
	}
): void => {
	state.backendSyncStatus = payload.status;
	if (payload.timestamp) {
		state.lastBackendSync = payload.timestamp;
	}
};

const applyToggleFavorite = (
	state: SearchHistoryStateInterface,
	payload: {
		module: string;
		query: string;
	}
): void => {
	if (state.historyByModule[payload.module]) {
		const entry = state.historyByModule[payload.module].find(
			(existingEntry) => existingEntry.query === payload.query
		);

		if (entry) {
			entry.isFavorite = !entry.isFavorite;

			if (entry.isFavorite) {
				const existingFavorites = state.historyByModule[payload.module].filter(
					(favorite) => favorite.isFavorite === true && favorite.query !== entry.query
				);
				const maxSortOrder = Math.max(...existingFavorites.map((favorite) => favorite.sortOrder ?? -1), -1);
				entry.sortOrder = maxSortOrder + 1;
			} else {
				entry.sortOrder = undefined;
			}

			persistHistoryByModule(state);
		}
	}
};

const applyUpdateFavoriteComplete = (
	state: SearchHistoryStateInterface,
	payload: {
		color?: string;
		displayInResults?: 'inline' | 'none' | 'separate';
		label?: string;
		module: string;
		newQuery: string;
		originalQuery: string;
	}
): void => {
	if (state.historyByModule[payload.module]) {
		const entryIndex = state.historyByModule[payload.module].findIndex(
			(entry) => entry.query === payload.originalQuery
		);

		if (entryIndex !== -1) {
			const entry = state.historyByModule[payload.module][entryIndex];
			state.historyByModule[payload.module][entryIndex] = {
				...entry,
				color: payload.color !== undefined ? payload.color : entry.color,
				displayInResults:
					payload.displayInResults !== undefined ? payload.displayInResults : entry.displayInResults,
				label: payload.label !== undefined ? payload.label : entry.label,
				query: payload.newQuery,
				timestamp: Date.now(),
			};

			if (state.activeFavoritesByModule[payload.module]) {
				const activeFavoriteIndex = state.activeFavoritesByModule[payload.module].findIndex(
					(favorite) => favorite.query === payload.originalQuery
				);

				if (activeFavoriteIndex !== -1) {
					state.activeFavoritesByModule[payload.module][activeFavoriteIndex] = {
						...state.activeFavoritesByModule[payload.module][activeFavoriteIndex],
						color:
							payload.color !== undefined
								? payload.color
								: state.activeFavoritesByModule[payload.module][activeFavoriteIndex].color,
						displayInResults:
							payload.displayInResults !== undefined
								? payload.displayInResults
								: state.activeFavoritesByModule[payload.module][activeFavoriteIndex].displayInResults,
						label:
							payload.label !== undefined
								? payload.label
								: state.activeFavoritesByModule[payload.module][activeFavoriteIndex].label,
						query: payload.newQuery,
					};
				}
			}

			persistHistoryByModule(state);
		}
	}
};

const applyUpdateFavoriteMetadata = (
	state: SearchHistoryStateInterface,
	payload: {
		color?: string;
		displayInResults?: 'inline' | 'none' | 'separate';
		label?: string;
		module: string;
		query: string;
	}
): void => {
	if (state.historyByModule[payload.module]) {
		const entry = state.historyByModule[payload.module].find(
			(existingEntry) => existingEntry.query === payload.query
		);

		if (entry) {
			if (payload.label !== undefined) {
				entry.label = payload.label;
			}
			if (payload.color !== undefined) {
				entry.color = payload.color;
			}
			if (payload.displayInResults !== undefined) {
				entry.displayInResults = payload.displayInResults;
			}

			persistHistoryByModule(state);
		}
	}
};

export const searchHistoryInternals = {
	applyAddSearchEntry,
	applyCleanEventMarkers,
	applyClearActiveFavoritesForModule,
	applyClearAllHistory,
	applyClearModuleHistory,
	applyClearNonFavoriteHistory,
	applyLoadHistoryFromStorage,
	applyMergeBackendData,
	applyRemoveSearchEntry,
	applyReorderFavorites,
	applySetActiveFavorite,
	applySetBackendSyncStatus,
	applyToggleFavorite,
	applyUpdateFavoriteComplete,
	applyUpdateFavoriteMetadata,
};

export const useSearchHistoryStore = defineStore('searchHistory', {
	actions: {
		addSearchEntry(payload: AddSearchEntryPayload): void {
			if (!payload.query.trim() || !payload.module.trim()) {
				return;
			}

			const entry: SearchHistoryEntry = {
				module: payload.module,
				query: payload.query.trim(),
				timestamp: Date.now(),
			};

			applyAddSearchEntry(this.$state, entry);
		},
		cleanEventMarkers(): void {
			applyCleanEventMarkers(this.$state);
		},
		clearActiveFavoritesForModule(module: string): void {
			applyClearActiveFavoritesForModule(this.$state, module);
		},
		clearAllHistory(payload?: ClearAllHistoryPayload): void {
			applyClearAllHistory(this.$state);

			if (payload?.syncToBackend && payload.commandBus && payload.queryStore) {
				void this.saveToBackend({
					commandBus: payload.commandBus,
					queryStore: payload.queryStore,
				});
			}
		},
		clearModuleHistory(payload: ClearModuleHistoryPayload): void {
			const module = typeof payload === 'string' ? payload : payload.module;
			const shouldSync = typeof payload === 'object' && payload.syncToBackend === true;

			applyClearModuleHistory(this.$state, module);

			if (shouldSync && typeof payload === 'object' && payload.commandBus && payload.queryStore) {
				void this.saveToBackend({
					commandBus: payload.commandBus,
					queryStore: payload.queryStore,
				});
			}
		},
		clearNonFavoriteHistory(payload: ClearNonFavoriteHistoryPayload): void {
			applyClearNonFavoriteHistory(this.$state, payload.module);

			if (payload.syncToBackend && payload.commandBus && payload.queryStore) {
				void this.saveToBackend({
					commandBus: payload.commandBus,
					queryStore: payload.queryStore,
				});
			}
		},
		getActiveFavoriteForModule(module: string): SearchHistoryEntry[] {
			if (!this.$state || !this.$state.activeFavoritesByModule) {
				console.warn('[Firefox Fix] SearchHistory state not properly initialized:', {
					module,
					state: this.$state,
				});
				return [];
			}

			const baseFavorites: Record<string, SearchHistoryEntry[]> = {};
			baseFavorites[module] = this.$state.activeFavoritesByModule[module] || [];

			const favoritesForModule = this.getFavoritesForModule(module);
			baseFavorites[module] = baseFavorites[module].filter((activeFavorite) => {
				const favoriteIndex = favoritesForModule.findIndex(
					(activeFavoriteForModule) =>
						activeFavoriteForModule.module == activeFavorite.module &&
						activeFavoriteForModule.query == activeFavorite.query
				);
				return favoriteIndex !== -1;
			});

			localStorage.setItem(
				instanceSpecificLocalStorageKey(ACTIVE_FAVORITES_BY_MODULE),
				JSON.stringify(baseFavorites)
			);

			if (baseFavorites[module].length > 0) {
				const filterStore = useFilterStore();
				const hiddenSearchTerm = filterStore.hiddenSearchTermForModule.get(module) || '';

				if (hiddenSearchTerm && baseFavorites[module].length > 0) {
					const firstFavorite = baseFavorites[module][0];
					if (hiddenSearchTerm.includes(firstFavorite.query) && hiddenSearchTerm !== firstFavorite.query) {
						const extendedFavorites = [...baseFavorites[module]];
						extendedFavorites[0] = {
							...firstFavorite,
							label: firstFavorite.label
								? `${firstFavorite.label}${hiddenSearchTerm.substring(firstFavorite.query.length)}`
								: undefined,
							query: hiddenSearchTerm,
						};
						return extendedFavorites;
					}
				}
			}

			return baseFavorites[module];
		},
		getAllHistory(): SearchHistoryByModule {
			return this.$state.historyByModule;
		},
		getBackendSyncStatus(): BackendSyncStatus {
			return this.$state.backendSyncStatus;
		},
		getFavoritesForModule(module: string): SearchHistoryEntry[] {
			const favorites = (this.$state.historyByModule[module] || []).filter((entry) => entry.isFavorite === true);

			const favoritesWithOrder = favorites.filter((favorite) => typeof favorite.sortOrder === 'number');
			const favoritesWithoutOrder = favorites.filter((favorite) => typeof favorite.sortOrder !== 'number');

			favoritesWithOrder.sort((a, b) => (a.sortOrder as number) - (b.sortOrder as number));

			favoritesWithoutOrder.sort((a, b) => {
				const labelA = a.label || a.query || '';
				const labelB = b.label || b.query || '';
				return labelA.localeCompare(labelB);
			});

			return [...favoritesWithOrder, ...favoritesWithoutOrder].map((favorite, index) => ({
				...favorite,
				sortOrder: index,
			}));
		},
		getHistoryForModule(module: string): SearchHistoryEntry[] {
			return this.$state.historyByModule[module] || [];
		},
		getLastBackendSync(): null | number {
			return this.$state.lastBackendSync;
		},
		hasHistoryForModule(module: string): boolean {
			return Boolean(this.$state.historyByModule[module] && this.$state.historyByModule[module].length > 0);
		},
		async loadFromBackend(payload: LoadFromBackendPayload): Promise<void> {
			applySetBackendSyncStatus(this.$state, {
				status: 'loading',
			});

			const stateProviderService = new StateProviderService(payload.queryStore, payload.commandBus);

			try {
				const backendStates = await stateProviderService.loadUserInterfaceStates();
				if (backendStates) {
					const { searchHistory } = stateProviderService.extractFavoritesFromStates(backendStates);
					applyMergeBackendData(this.$state, {
						historyByModule: searchHistory as SearchHistoryByModule,
					});
					applySetBackendSyncStatus(this.$state, {
						status: 'success',
					});
					return;
				}

				applySetBackendSyncStatus(this.$state, {
					status: 'idle',
				});
			} catch (error) {
				console.warn('Failed to load from backend:', error);
				applySetBackendSyncStatus(this.$state, {
					status: 'error',
				});
			}
		},
		loadHistoryFromStorage(payload: SearchHistoryByModule): void {
			applyLoadHistoryFromStorage(this.$state, payload);
		},
		async migrateLocalstorageToBackend(payload: LoadFromBackendPayload): Promise<boolean> {
			const hasLocalData = Object.keys(this.$state.historyByModule).length > 0;
			const neverSynced = this.$state.lastBackendSync === null;

			if (!hasLocalData || !neverSynced) {
				return false;
			}

			try {
				await this.syncWithBackend(payload);
				return true;
			} catch {
				return false;
			}
		},
		removeSearchEntry(payload: RemoveSearchEntryPayload): void {
			applyRemoveSearchEntry(this.$state, payload);
		},
		reorderFavorites(payload: ReorderFavoritesPayload): void {
			applyReorderFavorites(this.$state, {
				module: payload.module,
				reorderedFavorites: payload.reorderedFavorites,
			});

			if (payload.commandBus && payload.queryStore) {
				void this.saveToBackend({
					commandBus: payload.commandBus,
					queryStore: payload.queryStore,
				});
			}
		},
		async saveToBackend(payload?: SaveToBackendPayload): Promise<boolean> {
			if (!payload) {
				return false;
			}

			applySetBackendSyncStatus(this.$state, {
				status: 'loading',
			});

			const stateProviderService = new StateProviderService(payload.queryStore, payload.commandBus);

			try {
				const existingStates = await stateProviderService.loadUserInterfaceStates();
				const backendStates = existingStates || {};
				const updatedStates = stateProviderService.mergeStatesWithFavorites(
					backendStates,
					this.$state.historyByModule
				);
				const success = await stateProviderService.saveUserInterfaceStates(updatedStates);

				if (success) {
					applySetBackendSyncStatus(this.$state, {
						status: 'success',
						timestamp: Date.now(),
					});
					return true;
				}

				applySetBackendSyncStatus(this.$state, {
					status: 'error',
				});
				return false;
			} catch (error) {
				console.error('SAVE_TO_BACKEND: ❌ Fehler beim Backend-Save:', error);
				applySetBackendSyncStatus(this.$state, {
					status: 'error',
				});
				return false;
			}
		},
		setActiveFavorite(payload: SetActiveFavoritePayload): void {
			applySetActiveFavorite(this.$state, payload);
		},
		async syncWithBackend(payload: LoadFromBackendPayload): Promise<void> {
			await this.loadFromBackend(payload);
			await this.saveToBackend(payload);
		},
		toggleFavorite(payload: ToggleFavoritePayload): void {
			applyToggleFavorite(this.$state, payload);

			if (payload.queryStore && payload.commandBus) {
				void this.saveToBackend({
					commandBus: payload.commandBus,
					queryStore: payload.queryStore,
				});
			}
		},
		updateFavoriteComplete(payload: UpdateFavoriteCompletePayload): void {
			applyUpdateFavoriteComplete(this.$state, payload);

			if (payload.queryStore && payload.commandBus) {
				void this.saveToBackend({
					commandBus: payload.commandBus,
					queryStore: payload.queryStore,
				});
			}
		},
		updateFavoriteMetadata(payload: UpdateFavoriteMetadataPayload): void {
			applyUpdateFavoriteMetadata(this.$state, payload);

			if (payload.queryStore && payload.commandBus) {
				void this.saveToBackend({
					commandBus: payload.commandBus,
					queryStore: payload.queryStore,
				});
			}
		},
	},
	state: createSearchHistoryState,
});
export * from './interfaces';
export * from './types';
